import { type QueryClient, type QueryKey } from "@tanstack/react-query"
import { getQueryKey } from "@trpc/react-query"

import { api, type RouterInputs, type RouterOutputs } from "~/utils/api"
import {
  runPartialRefreshBatch,
  type PartialRefreshOps,
} from "~/utils/cacheUtils/runPartialRefresh"
import { stockOutTimesRefreshOps } from "~/utils/cacheUtils/stockOutTimesCacheHelpers"
import { filterDefined } from "~/utils/tsUtils"

type SummedReturns = RouterOutputs["expectedReturns"]["getSummedForecastedReturns"]
type SummedReturnsInput = RouterInputs["expectedReturns"]["getSummedForecastedReturns"]

/** A refreshed variant missing from the slice has no returns left, so it is dropped, not kept. */
function mergeSummedReturns(
  existing: SummedReturns,
  variantIds: string[],
  slice: SummedReturns,
): SummedReturns {
  const refreshed = new Set(variantIds)
  return [...existing.filter((r) => !refreshed.has(r.variantId)), ...slice]
}

/** Re-reads only `variantIds` for every cached warehouse/channel entry and splices them in. */
export function summedReturnsRefreshOps(
  utils: ReturnType<typeof api.useUtils>,
  queryClient: QueryClient,
  variantIds: string[],
): PartialRefreshOps<{ queryKey: QueryKey; slice: SummedReturns }[]> {
  const allEntries = getQueryKey(api.expectedReturns.getSummedForecastedReturns)
  const invalidate = (queryKey: QueryKey) => {
    void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false })
  }
  return {
    cancel: () => {},
    fetch: async () => {
      const slices = await Promise.all(
        queryClient
          .getQueryCache()
          .findAll({ queryKey: allEntries })
          .map(async ({ queryKey, state }) => {
            // Nothing to splice into, or a response in flight would land on top of the patch.
            if (state.data === undefined || state.fetchStatus === "fetching") {
              invalidate(queryKey)
              return undefined
            }
            const { input } = queryKey[1] as { input: SummedReturnsInput }
            const slice = await utils.client.expectedReturns.getSummedForecastedReturns.query({
              ...input,
              variantIds,
            })
            return { queryKey, slice }
          }),
      )
      return filterDefined(slices)
    },
    apply: (slices) => {
      for (const { queryKey, slice } of slices) {
        queryClient.setQueryData<SummedReturns>(
          queryKey,
          (old) => old && mergeSummedReturns(old, variantIds, slice),
        )
      }
    },
    fallback: () => invalidate(allEntries),
  }
}

/**
 * Both slices a ForecastedReturns event refreshes. Applied in one tick: each slice that lands on its
 * own re-derives the whole variant table.
 */
export function refreshForecastedReturnsDomainCache(
  utils: ReturnType<typeof api.useUtils>,
  queryClient: QueryClient,
  variantIds: string[],
) {
  return runPartialRefreshBatch([
    stockOutTimesRefreshOps(utils, variantIds),
    summedReturnsRefreshOps(utils, queryClient, variantIds),
  ])
}
